import { Link, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { UseCasePage } from '../../components/use-case/UseCasePage';
import { MarketingLandingShell } from '../../components/marketing/MarketingLandingShell';
import { PageSeo } from '../../components/seo/PageSeo';
import { usePrerenderData } from '../../context/PrerenderDataContext';
import { buildUseCasePageSchema } from '../../lib/schema';
import {
  getUseCaseBySlug,
  resolveUseCaseSeoImage,
  useCasePageMeta,
} from '../../lib/useCaseSanity';
import { useSanityFetch } from '../../lib/useSanityFetch';

export default function UseCaseRoutePage() {
  const { slug } = useParams<{ slug: string }>();
  const prerenderUseCase = usePrerenderData()?.useCase;
  const hasPrerenderUseCase = prerenderUseCase !== undefined;

  const { data: fetchedPage, loading, error } = useSanityFetch(
    () => getUseCaseBySlug(slug!),
    [slug],
    { enabled: !hasPrerenderUseCase },
  );

  const page = hasPrerenderUseCase ? prerenderUseCase : fetchedPage;
  const isLoading = !hasPrerenderUseCase && loading;

  if (!hasPrerenderUseCase && !loading && !error && fetchedPage === null) {
    return (
      <MarketingLandingShell>
        <div className="py-20 text-center">
          <p className="text-on-surface-variant">Page not found.</p>
          <Link
            to="/"
            className="mt-4 inline-block font-mono text-[10px] tracking-[0.2em] uppercase text-primary hover:text-[#ffcfbf]"
          >
            Back to home
          </Link>
        </div>
      </MarketingLandingShell>
    );
  }

  if (isLoading) {
    return (
      <MarketingLandingShell>
        <div className="flex justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary" aria-label="Loading page" />
        </div>
      </MarketingLandingShell>
    );
  }

  if (error || !page) {
    return (
      <MarketingLandingShell>
        <div className="py-20 text-center">
          <p className="text-on-surface-variant">Failed to load page.</p>
          <Link
            to="/"
            className="mt-4 inline-block font-mono text-[10px] tracking-[0.2em] uppercase text-primary hover:text-[#ffcfbf]"
          >
            Back to home
          </Link>
        </div>
      </MarketingLandingShell>
    );
  }

  const seoImage = resolveUseCaseSeoImage(page);
  const meta = useCasePageMeta(page);

  return (
    <>
      <PageSeo meta={meta} ogImage={seoImage} jsonLd={buildUseCasePageSchema(page, seoImage)} />
      <MarketingLandingShell>
        <UseCasePage data={page} />
      </MarketingLandingShell>
    </>
  );
}
